/* MatchSingleLinesMode.js */

"use strict";

const _ = require("lodash");
const AceUtils = require("../AceUtils");
const searchOptions = {
    backwards: true,
    wrap: true,
    caseSensitive: false,
    wholeWord: false,
    regExp: true,
    start: undefined,
    range: undefined,
};

class MatchSingleLinesMode {
    static getHighlightRules(regex, namedGroupsList, tokenColors) {
        return {
            start: [
                {
                    regex: regex,
                    token: this._returnTokenInMatchSingleLinesMode,
                    next: "matched_section2",
                    namedGroupsList: namedGroupsList,
                    tokenColors: tokenColors,
                },
            ],
            matched_section2: [
                {
                    regex: regex,
                    token: this._returnTokenInMatchSingleLinesMode,
                    next: "start",
                    namedGroupsList: namedGroupsList,
                    tokenColors: tokenColors,
                },
            ],
        };
    }

    static _returnTokenInMatchSingleLinesMode(...args) {
        let tokenArray = [];
        if (_.isEmpty(this.namedGroupsList)) {
            return this.next === "start" ? "matched_section2" : "matched_section1";
        }
        let highlightTokenCounter = 0;
        let addedGroupToken = "regex";
        for (let i = 0; i < args.length && addedGroupToken === "regex"; i++) {
            if (
                !_.isEmpty(args[i]) &&
                !this.namedGroupsList[i].startsWith("$$AddedGroup") &&
                !this.namedGroupsList[i].startsWith("$$NotANamedGroup")
            ) {
                addedGroupToken = "namedgroup_nohighlight";
            }
        }
        for (let i = 0; i < args.length; i++) {
            let token;
            if (this.namedGroupsList[i].startsWith("$$AddedGroup")) {
                token = addedGroupToken;
            } else if (this.namedGroupsList[i].startsWith("$$NotANamedGroup")) {
                token = "namedgroup_nohighlight";
            } else {
                token = this.tokenColors[highlightTokenCounter % this.tokenColors.length];
                highlightTokenCounter++;
            }
            tokenArray.push(token);
        }

        return tokenArray;
    }

    static getMatchCount(editorSession, regex) {
        if (!regex) {
            return 0;
        }

        let rowCount = editorSession.getLength();
        let matchCount = 0;
        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
            let rowString = editorSession.getLine(rowIndex);
            let currMatch = null,
                prevMatch = null;
            let regexpObj = new RegExp(regex, "g");
            while (!_.isNil((currMatch = regexpObj.exec(rowString)))) {
                if (!_.isEmpty(currMatch[0])) {
                    matchCount++;
                } else if (prevMatch && currMatch.index === prevMatch.index) {
                    //watch out for empty matches which pushes the regexp instance into infinite loops never moving beyond current empty match
                    //incrementing lastIndex avoids this scenario
                    regexpObj.lastIndex++;
                }
                prevMatch = currMatch;
            }
        }
        return matchCount;
    }

    static getCurrentMatchNumber(editor, editorSession, regex, endPosition, caseSensitive = false) {
        if (!regex) {
            return 0;
        }

        let matchCount = 0;
        for (let rowIndex = 0; rowIndex <= endPosition.row; rowIndex++) {
            let rowString = editorSession.getLine(rowIndex);
            let currMatch = null,
                prevMatch = null;
            let regexpObj = new RegExp(regex, "g" + (caseSensitive ? "" : "i"));
            while (!_.isNil((currMatch = regexpObj.exec(rowString)))) {
                if (!_.isEmpty(currMatch[0])) {
                    // current match starts before current selection range end
                    if (
                        AceUtils.comparePoints(endPosition, {
                            row: rowIndex,
                            column: currMatch.index,
                        }) > 0
                    ) {
                        matchCount++;
                    }
                } else if (prevMatch && currMatch.lastIndex === prevMatch.lastIndex) {
                    regexpObj.lastIndex++;
                }
                prevMatch = currMatch;
            }
        }
        return matchCount;
    }

    static getNextMatchNumber(
        editor,
        regex,
        currentMatchNumber,
        totalMatchCount,
        cursorPositionChanged = false,
        caseSensitive = true
    ) {
        if (
            totalMatchCount === 0 ||
            (totalMatchCount === 1 &&
                editor.getSelectedText() &&
                this._isValidMatch(editor.getSelectedText(), regex))
        ) {
            return totalMatchCount;
        }
        let editorSession = editor.getSession();
        let nextMatchSearchstartPosition = editor.getSelectionRange().end;
        let currentRowIndex = nextMatchSearchstartPosition.row;
        let lastRowIndex = editorSession.getLength() - 1;
        let nextMatch;
        let matchCountTillEndOfSelection = cursorPositionChanged
            ? this.getCurrentMatchNumber(
                  editor,
                  editorSession,
                  regex,
                  nextMatchSearchstartPosition,
                  caseSensitive
              )
            : currentMatchNumber;
        if (matchCountTillEndOfSelection === totalMatchCount) {
            nextMatchSearchstartPosition = {row: 0, column: 0};
            currentRowIndex = 0;
            matchCountTillEndOfSelection = 0;
        }
        let regexObj = new RegExp(regex, "g" + (caseSensitive ? "" : "i"));
        while (!nextMatch && currentRowIndex <= lastRowIndex) {
            let currentRowText = editorSession.getLine(currentRowIndex);
            let currentMatch;
            let prevMatch;
            while ((currentMatch = regexObj.exec(currentRowText)) !== null) {
                //if match is not an empty string && match start on or after current selection end
                if (
                    currentMatch[0] &&
                    AceUtils.comparePoints(nextMatchSearchstartPosition, {
                        row: currentRowIndex,
                        column: currentMatch.index,
                    }) <= 0
                ) {
                    let matchRange = AceUtils.getRangeInstance(
                        currentRowIndex,
                        currentMatch.index,
                        currentRowIndex,
                        currentMatch.index + currentMatch[0].length
                    );
                    // editor select match range & navigate to the line
                    editor.revealRange(matchRange);
                    nextMatch = currentMatch;
                    break;
                }
                //if regexObj is stuck at current index with an empty match, increment the lastIndex. If not this will cause infinite loop
                if (prevMatch && currentMatch.index === prevMatch.index) {
                    regexObj.lastIndex++;
                }
                prevMatch = currentMatch;
            }
            currentRowIndex++;
        }
        return matchCountTillEndOfSelection + 1;
    }

    static getPreviousMatchNumber(
        editor,
        regex,
        currentMatchNumber,
        totalMatchCount,
        cursorPositionChanged = false,
        caseSensitive = true
    ) {
        if (
            totalMatchCount === 0 ||
            (totalMatchCount === 1 &&
                editor.getSelectedText() &&
                this._isValidMatch(editor.getSelectedText(), regex))
        ) {
            return totalMatchCount;
        }
        let editorSession = editor.getSession();
        let startPosition = editor.getSelectionRange().start;
        let currentRowIndex = startPosition.row;
        let lastRowIndex = editorSession.getLength() - 1;
        let regexObj = new RegExp(regex, "g" + (caseSensitive ? "" : "i"));
        let previousMatch;
        let matchCountTillEndOfSelection = cursorPositionChanged
            ? this.getCurrentMatchNumber(editor, editorSession, regex, startPosition, caseSensitive)
            : currentMatchNumber;
        if (
            matchCountTillEndOfSelection === 0 ||
            (!cursorPositionChanged && matchCountTillEndOfSelection === 1)
        ) {
            startPosition = {row: lastRowIndex, column: editorSession.getLine(lastRowIndex).length};
            currentRowIndex = lastRowIndex;
            matchCountTillEndOfSelection = totalMatchCount + 1;
        } else if (cursorPositionChanged) {
            matchCountTillEndOfSelection++;
        }
        while (!previousMatch && currentRowIndex >= 0) {
            let currentRowText = editorSession.getLine(currentRowIndex);
            let currentMatch;
            let prevMatchOnCurrentLine;
            while ((currentMatch = regexObj.exec(currentRowText)) !== null) {
                // if current match starts before current selection start position
                if (
                    currentMatch[0] &&
                    (currentRowIndex < startPosition.row ||
                        (currentRowIndex === startPosition.row &&
                            currentMatch.index < startPosition.column))
                ) {
                    previousMatch = currentMatch;
                }
                if (prevMatchOnCurrentLine && currentMatch.index === prevMatchOnCurrentLine.index) {
                    regexObj.lastIndex++;
                }
                prevMatchOnCurrentLine = currentMatch;
            }
            if (previousMatch) {
                let previousMatchRange = AceUtils.getRangeInstance(
                    currentRowIndex,
                    previousMatch.index,
                    currentRowIndex,
                    previousMatch.index + previousMatch[0].length
                );
                editor.revealRange(previousMatchRange);
                break;
            }
            currentRowIndex--;
        }
        return matchCountTillEndOfSelection - 1;
    }

    static _isValidMatch(text, regex) {
        if (!text || !regex) {
            return false;
        }
        let match = text.match(regex);
        return match ? match[0] === text : false;
    }

    static _getSearchOptions(regex, startPosition = undefined) {
        let nextSearchOptions = _.clone(searchOptions);
        nextSearchOptions.needle = regex;
        nextSearchOptions.start = startPosition;
        return nextSearchOptions;
    }
}

module.exports = MatchSingleLinesMode;
